/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedHTMLMessage } from 'react-intl';

import type { FetchResult } from '@apollo/client';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { InlineNotification, Typography2 } from '@coursera/cds-core';
import type { SendVerificationEmailResponse } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1beta1/user_emails_api';

import { InviteAcceptanceContainer } from 'bundles/enterprise-admin-onboarding/components/InviteAcceptance/ui';
import { Button, LargeText, PersonallyIdentifiableInfo, Spacer } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/admin-dashboard';

const EmailIcon: React.FC<{}> = () => (
  <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M59.04 34.3355C59.04 47.5205 38.925 58.3355 25.74 58.3355C12.555 58.3355 11.295 47.5805 11.295 34.3355C11.295 21.0905 28.785 10.7705 42 10.7705C55.215 10.7705 59.04 21.1505 59.04 34.3355Z"
      fill="#D6EDF6"
    />
    <path d="M61.155 20.0996H10.86V51.9146H61.155V20.0996Z" stroke="black" strokeWidth="2" strokeLinejoin="round" />
    <path d="M10.86 20.0996L36 39.9746L61.14 20.0996" stroke="black" strokeWidth="2" strokeLinejoin="round" />
    <path d="M10.86 51.8993L26.28 32.2793" stroke="black" strokeWidth="2" strokeLinejoin="round" />
    <path d="M61.14 51.8993L45.72 32.2793" stroke="black" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

type State = { wasEmailResent: boolean; didFailToResend: boolean };
export class EmailVerification extends React.Component<
  {
    email: string;
    description?: string;
    callToAction?: React.ReactNode;
    onConfirm: () => Promise<string | undefined>;
    onResend: () => Promise<FetchResult<{ sendVerificationEmail: SendVerificationEmailResponse }>>;
  },
  State
> {
  static contextTypes = {
    addToast: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof EmailVerification.contextTypes>;

  state: State = { wasEmailResent: false, didFailToResend: false };

  handleConfirm = () => {
    const { addToast } = this.context;
    const { onConfirm } = this.props;
    onConfirm().then((errorCode?: string) => {
      if (errorCode === 'EMAIL_IS_NOT_VERIFIED' || errorCode === 'UNVERIFIED_EMAIL_ADDRESS') {
        addToast(_t('Email is still not verified'));
      }
    });
  };

  handleResend = () => {
    const { onResend } = this.props;

    return onResend()
      .then(() => {
        this.setState({ wasEmailResent: true, didFailToResend: false });
      })
      .catch(() => {
        this.setState({ didFailToResend: true, wasEmailResent: false });
      });
  };

  render() {
    const {
      email,
      description = _t('You must confirm your email address to become an administrator.'),
      callToAction = (
        <FormattedHTMLMessage
          message={_t(
            'Please check your <strong>{email}</strong> inbox for your Coursera account verification email. After the verification, please re-visit this link to gain access.'
          )}
          email={email}
        />
      ),
    } = this.props;
    const { wasEmailResent, didFailToResend } = this.state;
    return (
      <InviteAcceptanceContainer data-e2e="switch-account-for-admin-access">
        <Spacer size={64} />
        <EmailIcon />
        <Spacer size={48} />
        <Typography2 component="h1" variant="titleMedium">
          {_t('You’re almost there!')}
        </Typography2>
        <Spacer size={32} />
        <LargeText>{description}</LargeText>
        <Spacer size={12} />
        <LargeText>
          <PersonallyIdentifiableInfo>{callToAction}</PersonallyIdentifiableInfo>
        </LargeText>
        {wasEmailResent && (
          <div data-e2e="email-resent-notification">
            <Spacer size={24} />
            <InlineNotification severity="success">{_t('The verification email has been resent')}</InlineNotification>
          </div>
        )}
        {didFailToResend && (
          <div>
            <Spacer size={24} />
            <InlineNotification severity="error">
              {_t('There was an error sending the email, please try again later.')}
            </InlineNotification>
          </div>
        )}
        <Spacer size={32} />
        <section
          css={css`
            display: flex;
            gap: var(--cds-spacing-200);
          `}
        >
          <Button variant="primary" onClick={this.handleConfirm} data-testid="confirm">
            {_t('I’ve confirmed my email ')}
          </Button>
          {!wasEmailResent && (
            <Button variant="secondary" onClick={this.handleResend} data-e2e="resend">
              {_t('Resend confirmation email')}
            </Button>
          )}
        </section>
      </InviteAcceptanceContainer>
    );
  }
}
